import api from './serviceApi'; // Import the axios instance

const orderApi = {
    /**
     * Fetch all orders with optional filters.
     * @param {Object} params - Query parameters such as `include`.
     * @returns {Promise<Object>} - The list of orders.
     */
    getAllOrders: async (params = {}) => {
        try {
            const response = await api.get('/Order', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching orders:', error);
            throw error;
        }
    },

    /**
     * Fetch an order by its code.
     * @param {string} code - The code of the order.
     * @param {Object} params - Query parameters such as `include`.
     * @returns {Promise<Object>} - The order data.
     */
    getOrderByCode: async (code, params = {}) => {
        try {
            const response = await api.get(`/Order/${code}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching order by code:', error);
            throw error;
        }
    },

    /**
     * Fetch an order by its code.
     * @param {string} code - The code of the order.
     * @param {Object} params - Query parameters such as `include`.
     * @returns {Promise<Object>} - The order data.
     */
    getOrderIntegrationByCode: async (code, params = {}) => {
        try {
            const response = await api.get(`/Order/integration/${code}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching order integration by code:', error);
            throw error;
        }
    },

    /**
     * Fetch an order by its code.
     * @param {string} code - The code of the order.
     * @param {Object} params - Query parameters such as `include`.
     * @returns {Promise<Object>} - The order data.
     */
    updateOrderWithBemPaggo: async (code) => {
        try {
            const response = await api.post(`/Order/update-payment/${code}`, { });
            return response.data;
        } catch (error) {
            console.error('Error fetching order integration by code:', error);
            throw error;
        }
    },

    updatePixStatus: async (code) => {
        try {
            const response = await api.post(`/Order/update-pix/${code}`, { });
            return response.data;
        } catch (error) {
            console.error('Error fetching order pix by code:', error);
            throw error;
        }
    },

    /**
     * Fetch an order by its number.
     * @param {string} number - The number of the order.
     * @param {Object} params - Query parameters such as `include`.
     * @returns {Promise<Object>} - The order data.
     */
    getOrderByNumber: async (number, params = {}) => {
        try {
            const response = await api.get(`/Order/GetByNumber/${number}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching order by number:', error);
            throw error;
        }
    },

    /**
     * Fetch an order by its number.
     * @param {Object} params - Query parameters such as `include`.
     * @returns {Promise<Object>} - The order data.
     */
    GetOrderForClient: async (params = {}) => {
        try {
            const response = await api.get(`/Order/GetOrderForClient`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching order:', error);
            throw error;
        }
    },

    /**
     * Fetch an order for the logged client by its number.
     * @param {string} number - The number of the order.
     * @param {Object} params - Query parameters such as `include`.
     * @returns {Promise<Object>} - The order data.
     */
    GetOrderForClientByNumber: async (number, params = {}) => {
        try {
            const response = await api.get(`/Order/GetOrderForClientByNumber/${number}`, { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching order for client by number:', error);
            throw error;
        }
    },

    /**
     * Create a new order.
     * @param {Object} orderData - The order data to create.
     * @returns {Promise<Object>} - The created order data.
     */
    createOrder: async (orderData) => {
        try {
            const response = await api.post('/Order', orderData);
            return response.data;
        } catch (error) {
            console.error('Error creating order:', error);
            throw error;
        }
    },

    /**
     * Update an existing order.
     * @param {Object} orderData - The order data to update.
     * @returns {Promise<Object>} - The updated order data.
     */
    updateOrder: async (orderData) => {
        try {
            const response = await api.put('/Order', orderData);
            return response.data;
        } catch (error) {
            console.error('Error updating order:', error);
            throw error;
        }
    },

    /**
     * Delete an order by its code.
     * @param {string} orderCode - The order code to delete.
     * @returns {Promise<Object>} - The result of the deletion.
     */
    deleteOrder: async (orderCode) => {
        try {
            const response = await api.delete('/Order', {
                data: { code: orderCode },
            });
            return response.data;
        } catch (error) {
            console.error('Error deleting order:', error);
            throw error;
        }
    },

    /**
     * Export orders based on filters.
     * @param {Object} params - Query parameters such as `term`, `isActive`, etc.
     * @returns {Promise<Blob>} - The exported file data.
     */
    exportOrders: async (params) => {
        try {
            const response = await api.get('/Order/export', {
                params
            });
            return response.data;
        } catch (error) {
            console.error('Error exporting orders:', error);
            throw error;
        }
    },

    /**
     * Fetch a paginated list of orders with optional filters.
     * @param {Object} params - Query parameters such as `page`, `quantity`, `term`, etc.
     * @returns {Promise<Object>} - The paginated list of orders.
     */
    getOrdersPaginated: async (params) => {
        try {
            const response = await api.get('/Order/pagged', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching paginated orders:', error);
            throw error;
        }
    },

    /**
     * Fetch the last order number.
     * @returns {Promise<Object>} - The last order number.
     */
    getLastOrderNumber: async () => {
        try {
            const response = await api.get('/Order/LastNumber');
            return response.data;
        } catch (error) {
            console.error('Error fetching last order number:', error);
            throw error;
        }
    },
};

export default orderApi;
